import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { useAuth } from '../hooks/useAuth';
import type { 
  EmpresaUsuario, 
  EmpresaActivaResponse, 
  EstadisticasEmpresaResponse
} from '../types';

// CONTEXTO DE EMPRESAS PARA UAU

interface OrganizacionUAU {
  id: number;
  name: string;
}

// Mapeo de organización UAU a formato empresa MAVA
const mapearOrganizacionAEmpresa = (org: OrganizacionUAU): EmpresaUsuario => ({
  empresa_id: org.id,
  nombre: org.name,
  fecha_asignacion: new Date().toISOString(),
  rol_en_empresa: 'usuario',
} as unknown as EmpresaUsuario);

// CONTEXTO PARA EMPRESAS UAU

interface EmpresasContextTypeUAU {
  // Estado
  misEmpresas: EmpresaUsuario[];
  empresaActiva: EmpresaActivaResponse | null;
  estadisticas: EstadisticasEmpresaResponse | null;
  cargando: boolean;
  error: string | null;
  
  // Computed properties
  nombreEmpresaActiva: string;
  esAdminEmpresa: boolean;
  tieneMultiplesEmpresas: boolean;  // 🆕 AGREGADO
  
  // Acciones
  cambiarEmpresa: (empresaId: number) => Promise<void>;
  actualizarTodo: () => Promise<void>;
  cambiarEmpresaConInvalidacion: (empresaId: number) => Promise<void>;
}

const EmpresasContext = createContext<EmpresasContextTypeUAU | undefined>(undefined);

// PROVIDER DE EMPRESAS

interface EmpresasProviderProps {
  children: ReactNode;
}

export const EmpresasProvider: React.FC<EmpresasProviderProps> = ({ children }) => {
  const { usuario, organizaciones, empresaActiva: empresaActivaAuth } = useAuth();
  const queryClient = useQueryClient();
  
  // Estado local
  const [misEmpresas, setMisEmpresas] = useState<EmpresaUsuario[]>([]);
  const [empresaActiva, setEmpresaActiva] = useState<EmpresaActivaResponse | null>(null);
  const [estadisticas, setEstadisticas] = useState<EstadisticasEmpresaResponse | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Propiedades computadas

  const nombreEmpresaActiva = empresaActiva?.empresa?.nombre || '';
  const esAdminEmpresa = usuario?.tipo_usuario === 'administrador';
  const tieneMultiplesEmpresas = misEmpresas.length > 1;

  // Inicialización con datos UAU

  useEffect(() => {
    const inicializarConDatosUAU = async () => {
      try {
        setCargando(true);
        setError(null);
        
        // 1. Mapear organizaciones UAU a empresas MAVA
        let empresasMapeadas: EmpresaUsuario[] = [];
        
        // OBTENER ORGANIZACIONES DEL LOGIN UAU ORIGINAL (no crear temporales)
        const datosLoginUAU = JSON.parse(localStorage.getItem('uau_login_data') || '{}');
        const organizacionesUAU = datosLoginUAU.organizations || organizaciones || [];
        
        // REVISAR SI ORGANIZACIONES ESTÁN EN user_info
        const userInfoOrgs = datosLoginUAU.user_info?.organizaciones || datosLoginUAU.user_info?.organizations || [];
        
        // PRIORIZAR organizaciones de user_info
        const organizacionesFinales = userInfoOrgs.length > 0 ? userInfoOrgs : organizacionesUAU;
        
        if (organizacionesFinales && organizacionesFinales.length > 0) {
          empresasMapeadas = organizacionesFinales.map(mapearOrganizacionAEmpresa);
          
          // Ordenar empresas con prioridad (configurable)
          empresasMapeadas.sort((a, b) => {
          // Priorizar empresa principal
            if (a.empresa_id === 3) return -1;
            if (b.empresa_id === 3) return 1;
            return a.empresa_id - b.empresa_id;
          });
        } else if (usuario?.empresas_asignadas && usuario.empresas_asignadas.length > 0) {
          // Fallback: usar empresas del usuario
          empresasMapeadas = usuario.empresas_asignadas;
        } else {
          // Como último recurso, usar organización por defecto basada en nombre
          empresasMapeadas = [{
            empresa_id: 3,
            nombre: 'Forestal Mava',
            fecha_asignacion: new Date().toISOString(),
            rol_en_empresa: usuario?.tipo_usuario || 'usuario',
          } as unknown as EmpresaUsuario];
        }

        setMisEmpresas(empresasMapeadas);

        // Establecer empresa activa
        let empresaActivaAUtilizar: EmpresaActivaResponse;
        
        if (empresaActivaAuth) {
          // Usar empresa activa del auth
          empresaActivaAUtilizar = {
            empresa: empresaActivaAuth,
          } as unknown as EmpresaActivaResponse;
        } else if (empresasMapeadas.length > 0) {
          // Usar la primera empresa disponible
          const primeraEmpresa = empresasMapeadas[0];
          empresaActivaAUtilizar = {
            empresa: {
              id: primeraEmpresa.empresa_id,
              nombre: primeraEmpresa.nombre,
              rut: '',
            },
          } as unknown as EmpresaActivaResponse;
        } else {
          throw new Error('No hay empresas disponibles');
        }

        setEmpresaActiva(empresaActivaAUtilizar);

        // Guardar en localStorage
        localStorage.setItem('mava_empresa_activa', JSON.stringify(empresaActivaAUtilizar));

        // 3. Obtener estadísticas del endpoint dashboard
        try {
          const { ApiClient } = await import('../services/apiClient');
          const estadisticasReales = await ApiClient.get<any>('/dashboard/estadisticas');
          setEstadisticas(estadisticasReales as any);
        } catch (statsError) {
          setEstadisticas(null);
        }
      } catch (error: any) {
        setError(error.message || 'Error al cargar empresas');
      } finally {
        setCargando(false);
      }
    };

    // Solo inicializar si hay usuario autenticado
    if (usuario) {
      inicializarConDatosUAU();
    } else {
      setCargando(false);
    }
  }, [usuario, organizaciones, empresaActivaAuth]);

  // Acciones del contexto

  const cambiarEmpresa = async (empresaId: number): Promise<void> => {
    try {
      const empresaEncontrada = misEmpresas.find(emp => emp.empresa_id === empresaId);
      if (!empresaEncontrada) {
        throw new Error(`Empresa con ID ${empresaId} no encontrada`);
      }

      // Actualizar empresa activa
      const nuevaEmpresaActiva = {
        empresa: {
          id: empresaEncontrada.empresa_id,
          nombre: empresaEncontrada.nombre,
          rut: '',
        },
      } as unknown as EmpresaActivaResponse;

      setEmpresaActiva(nuevaEmpresaActiva);

      // Guardar en localStorage
      localStorage.setItem('mava_empresa_activa', JSON.stringify(nuevaEmpresaActiva));
    } catch (error: any) {
      setError(error.message || 'Error al cambiar empresa');
    }
  };

  const actualizarTodo = async (): Promise<void> => {
    // En arquitectura UAU, los datos vienen del contexto auth
    // Solo invalidamos queries para refrescar datos
    queryClient.invalidateQueries(['dashboard']);
  };

  const cambiarEmpresaConInvalidacion = async (empresaId: number): Promise<void> => {
    await cambiarEmpresa(empresaId);
    
    // Invalidar todas las queries relacionadas
    queryClient.invalidateQueries(['operadores']);
    queryClient.invalidateQueries(['faenas']);
    queryClient.invalidateQueries(['predios']);
    queryClient.invalidateQueries(['rodales']);
    queryClient.invalidateQueries(['insumos']);
    queryClient.invalidateQueries(['items-chequeo']);
    queryClient.invalidateQueries(['maquinas']);
    queryClient.invalidateQueries(['reportes']);
    queryClient.invalidateQueries(['dashboard']);
  };

  // Valor del contexto

  const contextValue: EmpresasContextTypeUAU = {
    // Estado
    misEmpresas,
    empresaActiva,
    estadisticas,
    cargando,
    error,
    
    // Computed
    nombreEmpresaActiva,
    esAdminEmpresa,
    tieneMultiplesEmpresas,  // 🆕 AGREGADO
    
    // Acciones
    cambiarEmpresa,
    actualizarTodo,
    cambiarEmpresaConInvalidacion
  };

  return (
    <EmpresasContext.Provider value={contextValue}>
      {children}
    </EmpresasContext.Provider>
  );
};

// Hook personalizado

export const useEmpresasContext = (): EmpresasContextTypeUAU => {
  const context = useContext(EmpresasContext);
  if (!context) {
    throw new Error('useEmpresasContext debe ser usado dentro de un EmpresasProvider');
  }
  return context;
};

// HOC para componentes que requieren empresa

interface WithEmpresaProps {
  empresaActiva: EmpresaActivaResponse;
  esAdminEmpresa: boolean;
  nombreEmpresa: string;
}

export const withEmpresa = <P extends object>(
  Component: React.ComponentType<P & WithEmpresaProps>
) => {
  return React.forwardRef<any, P>((props, ref) => {
    const { empresaActiva, esAdminEmpresa, nombreEmpresaActiva } = useEmpresasContext();

    if (!empresaActiva) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando empresa...</p>
          </div>
        </div>
      );
    }

    return (
      <Component
        {...(props as any)}
        ref={ref}
        empresaActiva={empresaActiva}
        esAdminEmpresa={esAdminEmpresa}
        nombreEmpresa={nombreEmpresaActiva}
      />
    );
  });
};

// Componente de estado de empresa

interface EstadoEmpresaProps {
  mostrarDetalles?: boolean;
  className?: string;
}

export const EstadoEmpresa: React.FC<EstadoEmpresaProps> = ({ 
  mostrarDetalles = false, 
  className = "" 
}) => {
  const { 
    empresaActiva, 
    cargando, 
    error, 
    nombreEmpresaActiva,
    esAdminEmpresa 
  } = useEmpresasContext();

  if (cargando) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Cargando empresa...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <span className="text-red-600 text-sm">❌ {error}</span>
      </div>
    );
  }

  if (!empresaActiva) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <span className="text-yellow-600 text-sm">⚠️ Sin empresa activa</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className="text-green-600">🏢</span>
      <span className="text-sm font-medium text-gray-900">
        {nombreEmpresaActiva}
      </span>
      {esAdminEmpresa && (
        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          Admin
        </span>
      )}
      {mostrarDetalles && empresaActiva && (
        <span className="text-xs text-gray-500">
          (UAU Org)
        </span>
      )}
    </div>
  );
};

export default EmpresasContext;
